"use client";

import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Disc3,
  ListMusic,
  Loader2,
  X,
} from "lucide-react";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { setProfileSongs } from "@/actions/music";
import { ChatDrawerHeader } from "@/components/chat/chat-ui-primitives";
import { MusicBrowser, musicKeys } from "@/components/music/music-browser";
import { Button } from "@/components/ui/button";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { profileKeys } from "@/features/profile/queries/keys";
import { musicArtworkSrc } from "@/lib/music/links";
import {
  type MusicSong,
  PROFILE_SONG_FEATURED,
  PROFILE_SONG_MAX,
  type ProfileMusicSong,
} from "@/lib/music/types";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";

export { musicKeys } from "@/components/music/music-browser";

type PickedSong = Pick<
  MusicSong,
  "id" | "title" | "artistName" | "artworkUrl" | "previewUrl" | "explicit"
>;

function fromProfileSong(song: ProfileMusicSong): PickedSong {
  return {
    id: song.providerSongId,
    title: song.title,
    artistName: song.artistName,
    artworkUrl: song.artworkUrl,
    previewUrl: song.previewUrl,
    explicit: song.explicit,
  };
}

/**
 * Pick the songs for your profile. Opened from the profile (the "Add music"
 * card or Edit), from Edit profile, and from onboarding step 5 as an optional
 * extra (components/onboarding/onboarding-music-card.tsx).
 *
 * Works with no account connected: catalog search, the storefront's top songs
 * and artist/album drill-ins all run on Hiive's developer token. On the web,
 * connecting Apple Music adds "Recently played" as a shortcut.
 */
export function MusicPickerDrawer({
  open,
  onOpenChange,
  initialSongs,
  initialMode = "browse",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSongs: ProfileMusicSong[];
  /** "list" opens straight on the full list (from a "+N more" affordance). */
  initialMode?: "browse" | "list";
}) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<PickedSong[]>([]);
  const [mode, setMode] = useState<"browse" | "list">(initialMode);
  const [saving, setSaving] = useState(false);

  // Seed from the saved songs once per opening — never on a re-render, or a
  // fresh `initialSongs` array would wipe the picks mid-edit.
  const initialRef = useRef(initialSongs);
  initialRef.current = initialSongs;
  const initialModeRef = useRef(initialMode);
  initialModeRef.current = initialMode;
  useEffect(() => {
    if (!open) return;
    setPicked(initialRef.current.map(fromProfileSong));
    setMode(initialModeRef.current);
  }, [open]);

  const pickedIds = new Set(picked.map((song) => song.id));
  const full = picked.length >= PROFILE_SONG_MAX;
  const initialIds = initialSongs.map((song) => song.providerSongId).join(",");
  const dirty = picked.map((song) => song.id).join(",") !== initialIds;

  const togglePick = (song: MusicSong) => {
    if (pickedIds.has(song.id)) {
      setPicked((current) => current.filter((p) => p.id !== song.id));
      return;
    }
    if (full) {
      toast.message(`Your list holds up to ${PROFILE_SONG_MAX} songs`);
      return;
    }
    uiHaptics.select();
    // Once the display is full, a new song goes to the list — say where it
    // went, so it never looks like the tap did nothing.
    if (picked.length >= PROFILE_SONG_FEATURED) {
      toast.message(`Saved to your list as #${picked.length + 1}`, {
        description: "Move it up anytime to show it on your profile.",
      });
    }
    setPicked((current) => [...current, song]);
  };

  const move = (from: number, to: number) => {
    setPicked((current) => {
      if (to < 0 || to >= current.length || from === to) return current;
      const next = [...current];
      const [song] = next.splice(from, 1);
      next.splice(to, 0, song);
      return next;
    });
  };

  const makeFirst = (id: string) => {
    setPicked((current) => {
      const song = current.find((p) => p.id === id);
      return song ? [song, ...current.filter((p) => p.id !== id)] : current;
    });
  };

  const save = async (songIds: string[]) => {
    setSaving(true);
    try {
      const result = await setProfileSongs({ songIds });
      if (!result.ok) {
        uiHaptics.error();
        toast.error(result.message);
        return;
      }
      uiHaptics.success();
      if (result.data.unavailableIds.length > 0) {
        toast.message(
          "Some songs aren't available in your region and were skipped.",
        );
      } else {
        toast.success(
          songIds.length === 0
            ? "Music removed"
            : "Your music is on your profile",
        );
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: profileKeys.currentProfile(),
        }),
        queryClient.invalidateQueries({ queryKey: musicKeys.mine() }),
      ]);
      router.refresh();
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
      dismissible={!saving}
      repositionInputs={false}
    >
      <DrawerContent className="mx-auto h-[calc(90dvh-env(safe-area-inset-top))] max-w-2xl data-[vaul-drawer-direction=bottom]:rounded-t-3xl">
        <DrawerTitle className="sr-only">Music picks</DrawerTitle>
        {/* Hidden, not unmounted, while the list is open: the search and
            drill-in survive a trip to "Your list" and back. */}
        <div className={mode === "browse" ? "contents" : "hidden"}>
          <MusicBrowser
            open={open}
            title="Music picks"
            subtitle={
              picked.length > PROFILE_SONG_FEATURED
                ? `${picked.length} songs · ${PROFILE_SONG_FEATURED} on your profile`
                : `${picked.length} of ${PROFILE_SONG_FEATURED} on your profile`
            }
            topSlot={
              <PickedStrip
                picked={picked}
                onRemove={(id) =>
                  setPicked((current) => current.filter((p) => p.id !== id))
                }
                onMakeFirst={makeFirst}
                onOpenList={() => setMode("list")}
              />
            }
            isSelected={(id) => pickedIds.has(id)}
            selectionFull={full}
            onSelect={togglePick}
            previewSurface="picker"
          />
        </div>
        {mode === "list" ? (
          <ListManager
            picked={picked}
            onBack={() => setMode("browse")}
            onMove={move}
            onRemove={(id) =>
              setPicked((current) => current.filter((p) => p.id !== id))
            }
          />
        ) : null}

        <div className="flex flex-col gap-2 border-t border-border px-4 pt-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] dark:border-glass-border-10">
          <Button
            className="h-11 w-full rounded-full bg-text-primary text-background dark:bg-white dark:text-black"
            disabled={!dirty || saving}
            onClick={() => save(picked.map((song) => song.id))}
          >
            {saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {picked.length === 0 && initialSongs.length > 0
              ? "Remove music from profile"
              : "Save"}
          </Button>
          {initialSongs.length > 0 && picked.length > 0 ? (
            <button
              type="button"
              disabled={saving}
              onClick={() => save([])}
              className="py-1 text-xs text-text-tertiary underline-offset-2 pointer-fine:hover:underline"
            >
              Remove music from my profile
            </button>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

/**
 * Always five slots, filled or dashed, at a fixed height: the list below must
 * not move when the first song lands — that shift put the next tap on the
 * wrong row.
 */
function PickedStrip({
  picked,
  onRemove,
  onMakeFirst,
  onOpenList,
}: {
  picked: PickedSong[];
  onRemove: (id: string) => void;
  onMakeFirst: (id: string) => void;
  onOpenList: () => void;
}) {
  const extra = picked.length - PROFILE_SONG_FEATURED;
  const slots = Array.from({ length: PROFILE_SONG_FEATURED }, (_, index) => ({
    index,
    song: picked[index] ?? null,
  }));
  return (
    <div className="flex shrink-0 flex-col gap-1.5 pb-3">
      <div className="flex justify-center gap-2.5 px-4 pt-1.5">
        {slots.map(({ index, song }) => {
          if (!song) {
            return (
              <div key={`empty-${index}`} className="w-14 shrink-0">
                <div className="flex size-14 items-center justify-center rounded-xl border border-dashed border-border text-[11px] text-text-tertiary dark:border-glass-border-10">
                  {index + 1}
                </div>
                <p className="mt-1 h-3.5" />
              </div>
            );
          }
          const art = musicArtworkSrc(song.artworkUrl, 112);
          return (
            <div key={song.id} className="relative w-14 shrink-0">
              <button
                type="button"
                onClick={() => onMakeFirst(song.id)}
                aria-label={
                  index === 0
                    ? `${song.title} is your #1`
                    : `Make ${song.title} your #1`
                }
                className={cn(
                  "relative block size-14 overflow-hidden rounded-xl bg-surface-alt shadow-sm dark:bg-glass-bg-10",
                  index === 0 && "ring-2 ring-[#ffe14d]",
                )}
              >
                {art ? (
                  // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                  <img
                    src={art}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <Disc3 className="m-auto size-6 text-text-tertiary" />
                )}
                <span className="absolute bottom-1 left-1 rounded bg-black/70 px-1 text-[10px] font-bold text-white">
                  {index === 0 ? "#1" : index + 1}
                </span>
              </button>
              <button
                type="button"
                onClick={() => onRemove(song.id)}
                aria-label={`Remove ${song.title}`}
                className="absolute -top-1.5 -right-1.5 flex size-5 items-center justify-center rounded-full bg-text-primary text-background shadow"
              >
                <X className="size-3" strokeWidth={3} />
              </button>
              <p className="mt-1 h-3.5 truncate text-[10px] text-text-secondary">
                {song.title}
              </p>
            </div>
          );
        })}
      </div>
      {/* One fixed-height row either way, so nothing below moves when the
          list grows past the display. */}
      <div className="flex h-7 items-center justify-center px-4">
        {picked.length > 0 ? (
          <button
            type="button"
            onClick={onOpenList}
            className="flex h-7 items-center gap-1.5 rounded-full border border-border bg-surface-alt px-3 text-[11px] font-semibold text-text-secondary transition-transform duration-150 ease-out-strong active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-10 dark:text-white"
          >
            <ListMusic className="size-3.5" />
            {extra > 0
              ? `Your list · +${extra} more saved`
              : `Your list · ${picked.length} ${picked.length === 1 ? "song" : "songs"}`}
            <ChevronRight className="size-3.5 opacity-60" />
          </button>
        ) : (
          <p className="text-center text-[10px] text-text-tertiary">
            Add songs that say something about you.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * The whole list, in order. The top `PROFILE_SONG_FEATURED` are the cases on
 * the profile; everything under the divider is saved for later — still public
 * behind "+N", just not on display. Arrows reorder; "Show" lifts a saved song
 * into the last display slot.
 */
function ListManager({
  picked,
  onBack,
  onMove,
  onRemove,
}: {
  picked: PickedSong[];
  onBack: () => void;
  onMove: (from: number, to: number) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <>
      <ChatDrawerHeader
        title="Your list"
        subtitle={`${picked.length} ${picked.length === 1 ? "song" : "songs"} · top ${PROFILE_SONG_FEATURED} on your profile`}
        onBack={onBack}
      />
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pb-4">
        {picked.length === 0 ? (
          <p className="px-4 py-10 text-center text-sm text-text-tertiary">
            Your list is empty. Go back to add songs.
          </p>
        ) : null}
        {picked.map((song, index) => {
          const art = musicArtworkSrc(song.artworkUrl, 88);
          const onDisplay = index < PROFILE_SONG_FEATURED;
          return (
            <div key={song.id}>
              {index === 0 ? (
                <ListDivider label="On your profile" />
              ) : index === PROFILE_SONG_FEATURED ? (
                <ListDivider label="Saved in your list" />
              ) : null}
              <div className="flex h-14 items-center gap-2.5 rounded-xl px-2">
                <span className="w-5 shrink-0 text-center text-xs font-semibold text-text-tertiary tabular-nums">
                  {index + 1}
                </span>
                <span className="size-10 shrink-0 overflow-hidden rounded-lg bg-surface-alt dark:bg-glass-bg-10">
                  {art ? (
                    // biome-ignore lint/performance/noImgElement: Apple CDN, exact size requested.
                    <img
                      src={art}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Disc3 className="m-auto mt-2.5 size-5 text-text-tertiary" />
                  )}
                </span>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium text-text-primary">
                    {song.title}
                  </span>
                  <span className="truncate text-xs text-text-secondary">
                    {song.artistName}
                  </span>
                </span>
                {onDisplay ? null : (
                  <button
                    type="button"
                    onClick={() => onMove(index, PROFILE_SONG_FEATURED - 1)}
                    className="h-7 shrink-0 rounded-full bg-text-primary px-2.5 text-[11px] font-semibold text-background active:scale-95"
                  >
                    Show
                  </button>
                )}
                <ListIconButton
                  label={`Move ${song.title} up`}
                  disabled={index === 0}
                  onClick={() => onMove(index, index - 1)}
                >
                  <ArrowUp className="size-4" />
                </ListIconButton>
                <ListIconButton
                  label={`Move ${song.title} down`}
                  disabled={index === picked.length - 1}
                  onClick={() => onMove(index, index + 1)}
                >
                  <ArrowDown className="size-4" />
                </ListIconButton>
                <ListIconButton
                  label={`Remove ${song.title}`}
                  onClick={() => onRemove(song.id)}
                >
                  <X className="size-4" />
                </ListIconButton>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ListDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-2 pt-3 pb-1">
      <span className="text-[11px] font-bold tracking-wider text-text-tertiary uppercase">
        {label}
      </span>
      <span className="h-px flex-1 bg-border dark:bg-glass-border-10" />
    </div>
  );
}

function ListIconButton({
  label,
  disabled = false,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="pointer-fine:hover:bg-surface-hover flex size-8 shrink-0 items-center justify-center rounded-full text-text-secondary transition-transform duration-150 ease-out-strong active:scale-90 disabled:opacity-25 dark:text-white"
    >
      {children}
    </button>
  );
}
